import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectPortalSession } from "../../../store/slice/portalAuthSlice";

/**
 * Portal currency amount formatter using company `currency_decimals`.
 *
 * @returns {(amount: unknown, precisionOverride?: number|null) => string|null}
 */
export const usePortalFormattedAmount = () => {
  const { subdomain } = useParams();
  const session = useSelector(state => selectPortalSession(state, subdomain));

  const precision = session?.decimalPrecision?.currency_decimals ?? 2;

  const formatPortalAmount = useCallback(
    (amount, precisionOverride = null) => {
      if (amount === null || amount === undefined) return null;
      const effectivePrecision = precisionOverride !== null ? precisionOverride : precision;
      const numericAmount = Number(amount);
      if (isNaN(numericAmount)) return null;
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: effectivePrecision,
        maximumFractionDigits: effectivePrecision,
      }).format(numericAmount);
    },
    [precision],
  );

  return formatPortalAmount;
};

/**
 * Portal currency symbol for price labels.
 *
 * Login/autologin session `currency`: customer trading currency when set,
 * otherwise company currency. Falls back to `$` only when session has no
 * currency metadata.
 *
 * @returns {string}
 */
export const usePortalCurrencySymbol = () => {
  const { subdomain } = useParams();
  const session = useSelector(state => selectPortalSession(state, subdomain));
  const currency = session?.currency;
  if (currency?.symbol) return currency.symbol;
  if (currency?.code) return currency.code;
  return "$";
};

/**
 * Portal quantity formatter using company `quantity_decimals`.
 *
 * Used for catalogue qty inputs and quantity-tier range labels.
 *
 * @returns {(quantity: unknown, precisionOverride?: number|null) => string|null}
 */
export const usePortalFormattedQuantity = () => {
  const { subdomain } = useParams();
  const session = useSelector(state => selectPortalSession(state, subdomain));

  const precision = session?.decimalPrecision?.quantity_decimals ?? 2;

  const formatPortalQuantity = useCallback(
    (quantity, precisionOverride = null) => {
      if (quantity === null || quantity === undefined) return null;
      const effectivePrecision = precisionOverride !== null ? precisionOverride : precision;
      const numericQty = Number(quantity);
      if (isNaN(numericQty)) return null;
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: effectivePrecision,
        maximumFractionDigits: effectivePrecision,
      }).format(numericQty);
    },
    [precision],
  );

  return formatPortalQuantity;
};
